import {Component, computed, effect, inject, OnInit} from '@angular/core';
import {TaAppStore} from '../../../../state/clcta-store';
import {TaSubmissionDatasource} from '../../../../datasources/ta-submission.datasource';
import {TAStatus} from '../../../../api';
import {ManageTaApprovalComponent} from '../manage-ta-approval/manage-ta-approval.component';
import {ManageTaAcquittalComponent} from '../manage-ta-acquittal/manage-ta-acquittal.component';
import {Card} from 'primeng/card';
import {
  ManageTaAcquittalApprovalComponent
} from '../manage-ta-acquittal-approval/manage-ta-acquittal-approval.component';
import {
  ManageTaCancelApprovalComponent
} from '../manage-ta-cancel-approval/manage-ta-cancel-approval.component';
import {
  ManageTaCancelFinaliseComponent
} from '../manage-ta-cancel-finalise/manage-ta-cancel-finalise.component';
import {ManageTaInfoComponent} from '../manage-ta-info/manage-ta-info.component';
import {CANCEL_FLOW_STATUSES, toTAStatusText} from '../../../../util/app.types';
import {Button} from 'primeng/button';
import {TaAuditLogButtonComponent} from '../../ta-audit-log-button/ta-audit-log-button.component';
import {ConfirmationWrapperService} from '../../../../services/confirmation-wrapper.service';
import {PermissionService} from '../../../../services/permission.service';
import {Router} from '@angular/router';
import {ManageTaFinaliseComponent} from '../manage-ta-finalise/manage-ta-finalise.component';
import {ManageTaTravelApprovedComponent} from '../manage-ta-travel-approved/manage-ta-travel-approved.component';
import {
  ManageTaPaymentActionsComponent
} from '../manage-ta-payment-actions/manage-ta-payment-actions.component';
import {
  ManageTaDayTripBreakdownComponent
} from '../manage-ta-day-trip-breakdown/manage-ta-day-trip-breakdown.component';
import {
  ItineraryDifferenceComparisonComponent
} from '../itinerary-difference-comparison/itinerary-difference-comparison.component';
import {TaApprovalsTableComponent} from '../ta-approvals-table/ta-approvals-table.component';

@Component({
  selector: 'manage-ta-container',
  imports: [
    ManageTaApprovalComponent,
    ManageTaAcquittalComponent,
    ManageTaAcquittalApprovalComponent,
    ManageTaCancelApprovalComponent,
    ManageTaCancelFinaliseComponent,
    Card,
    ManageTaInfoComponent,
    Button,
    TaAuditLogButtonComponent,
    ManageTaFinaliseComponent,
    ManageTaTravelApprovedComponent,
    ManageTaPaymentActionsComponent,
    ManageTaDayTripBreakdownComponent,
    ItineraryDifferenceComparisonComponent,
    TaApprovalsTableComponent,
  ],
  templateUrl: './manage-ta-container.component.html',
  styleUrl: './manage-ta-container.component.scss'
})
export class ManageTaContainerComponent implements OnInit {
  store = inject(TaAppStore);
  taSubmissionDatasource = inject(TaSubmissionDatasource);
  confirmationServiceWrapper = inject(ConfirmationWrapperService);
  permissionService = inject(PermissionService);
  router = inject(Router);
  taStatus= computed<TAStatus | undefined>(() => this.store.taSubmission()?.status);
  canCancel = computed<boolean>(() => this.permissionService.canCancel());

  constructor() {
    // If the user manual enters the manage page when the TA is in progress
    // they get auto routed to the review version of the same page. Same for create-ta-page.
    effect(() => {
      const status = this.taStatus();
      const id = this.store.taSubmission()?.id;
      if (status === 'InProgress' && this.permissionService.canEditTaPrimary() && id) {
        this.router.navigate([`/ta/${id}/review`]);
      }
    });
  }

  ngOnInit() {
    this.store.updateBreadCrumbData({
      title: 'Manage TA',
      taNumberPrefix: 'Manage ',
      titleIcon: 'fa-regular fa-location-dot fa-lg',
      showTaComments: true,
    });
    // The container renders exactly one status branch, so it owns the estimate fetch for the
    // whole manage page - the branches below read store.taValue rather than requesting it
    // themselves. Runs before the branch is created, so the estimate is queued first.
    //
    // A TA in the cancellation flow is skipped: neither cancel page reads store.taValue - both take
    // the TA's own currentValue - and once the cancellation is approved the itinerary is zeroed, so
    // an estimate request would only re-derive the pre-cancel values from the rate tables.
    const submissionId = this.store.taSubmission()?.id;
    const status = this.taStatus();
    if (!!submissionId && !(!!status && CANCEL_FLOW_STATUSES.includes(status))) {
      this.taSubmissionDatasource.updateTaEstimateValue(submissionId);
    }
  };

  reviewTa(): void {
    this.router.navigate([`/ta/${this.store.taSubmission()?.id}/review`]);
  }

  viewTa(): void {
    this.router.navigate([`/ta/${this.store.taSubmission()?.id}/view`]);
  }

  cancelSubmissionClicked(): void {
    this.confirmationServiceWrapper.cancelConfirm(this.store.taSubmission()).subscribe({
      next: () => {},
      error: err => {
        console.log(err);
      }
    });
  }

  protected readonly toTAStatusText = toTAStatusText;
}
